import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  Plane,
  FileText,
  CreditCard,
  MessageSquare,
  DollarSign,
  Shield,
  Car,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useState } from 'react';
import logo from '../assets/logotransparent.png';

const Layout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    const result = await logout();
    if (result.success) {
      navigate('/admin/login');
    }
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    { name: 'Users', href: '/admin/users', icon: Users },
    { name: 'Trips', href: '/admin/trips', icon: Plane },
    { name: 'Requests', href: '/admin/requests', icon: FileText },
    { name: 'Transactions', href: '/admin/transactions', icon: CreditCard },
    { name: 'Fees', href: '/admin/fees', icon: DollarSign },
    { name: 'Messages', href: '/admin/messages', icon: MessageSquare },
    { name: 'Support', href: '/admin/support', icon: MessageSquare },
    { name: 'Verifications', href: '/admin/verifications', icon: Shield },
    {
      name: 'Driver Verifications',
      href: '/admin/driver-verifications',
      icon: Car,
    },
  ];

  return (
    <div className="min-h-screen bg-yellow-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-30 h-full w-64 bg-black shadow-2xl transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-yellow-600">
            <div className="flex items-center space-x-3">
              <img src={logo} alt="FlexySwap" className="h-10 w-10" />
              <h1 className="text-2xl font-bold text-yellow-400">FlexySwap</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-yellow-400 hover:text-yellow-300"
            >
              <X size={24} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navigation.map(item => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-yellow-500 text-black font-semibold'
                      : 'text-yellow-300 hover:bg-yellow-900 hover:text-yellow-200'
                  }`}
                >
                  <Icon size={20} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>

          {/* User info and logout */}
          <div className="p-4 border-t border-yellow-600">
            <div className="flex items-center space-x-3 mb-3 px-4 py-2">
              <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center">
                <span className="text-black font-semibold">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-yellow-300 truncate">
                  {user?.email}
                </p>
                <p className="text-xs text-yellow-400">Admin</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-yellow-300 hover:bg-yellow-900 hover:text-yellow-200 transition-colors"
            >
              <LogOut size={20} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="bg-black border-b border-yellow-600 shadow-lg sticky top-0 z-10">
          <div className="flex items-center justify-between px-6 py-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-yellow-400 hover:text-yellow-300"
            >
              <Menu size={24} />
            </button>
            <h2 className="text-xl font-semibold text-yellow-400">
              {navigation.find(item => item.href === location.pathname)?.name ||
                'Dashboard'}
            </h2>
            <div className="w-6" /> {/* Spacer for mobile */}
          </div>
        </header>

        {/* Page content */}
        <main className="p-6 bg-yellow-50 min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
